import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { SupportTicket, ChamadoStatus, SugestaoStatus } from '@/types/support';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { useRealtimeRefetch } from '@/hooks/useRealtimeRefetch';

function mapTicketFromDb(row: any): SupportTicket {
  return {
    id: row.id,
    type: row.type,
    origin: row.origin === 'contato_publico' ? 'contato-publico' : 'ajuda-interna',
    status: row.status,
    name: row.name,
    email: row.email,
    phone: row.phone,
    isClient: row.is_client,
    plan: row.plan,
    userId: row.user_id,
    subject: row.subject,
    message: row.message,
    createdAt: row.created_at,
    ticketNumber: row.ticket_number,
    lastMessageAt: row.last_message_at,
    unreadForAdmin: row.unread_for_admin,
    unreadForUser: row.unread_for_user,
    closedAt: row.closed_at,
  };
}

interface SupportContextType {
  /** Todos os chamados visíveis para quem está logado (admin vê tudo). */
  tickets: SupportTicket[];
  /** Somente os chamados abertos pela própria pessoa — inclusive quando ela é admin. */
  myTickets: SupportTicket[];
  loading: boolean;
  addTicket: (ticket: Omit<SupportTicket, 'id' | 'createdAt'>) => SupportTicket;
  updateTicketStatus: (id: string, status: ChamadoStatus | SugestaoStatus) => void;
  sendMessage: (ticketId: string, body: string, authorRole: 'admin' | 'cliente') => Promise<boolean>;
  markTicketRead: (ticketId: string) => Promise<void>;
  refresh: () => void;
}

const SupportContext = createContext<SupportContextType | undefined>(undefined);

export const SupportProvider = ({ children }: { children: ReactNode }) => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const { session, user } = useUser();

  const load = useCallback(async () => {
    setLoading(true);
    if (session?.user) {
      const query = user?.isAdmin
        ? supabase.from('support_tickets').select('*').order('created_at', { ascending: false })
        : supabase.from('support_tickets').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false });
      const { data } = await query;
      setTickets((data || []).map(mapTicketFromDb));
    } else {
      setTickets([]);
    }
    setLoading(false);
  }, [session?.user?.id, user?.isAdmin]);

  useEffect(() => {
    load();
  }, [session?.user?.id, user?.isAdmin, load]);

  useRealtimeRefetch(['support_tickets'], () => {
    if (session?.user) load();
  });

  const addTicket = useCallback((data: Omit<SupportTicket, 'id' | 'createdAt'>): SupportTicket => {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const ticket: SupportTicket = { ...data, id, createdAt: now };
    setTickets(prev => [ticket, ...prev]);

    const originDb = data.origin === 'contato-publico' ? 'contato_publico' : 'ajuda_interna';
    supabase.from('support_tickets').insert({
      id, type: data.type, origin: originDb, status: data.status,
      name: data.name, email: data.email, phone: data.phone,
      is_client: data.isClient, plan: data.plan,
      user_id: session?.user?.id || null,
      subject: data.subject, message: data.message,
    }).then(({ error }) => {
      if (error) { console.error('Erro ao criar chamado:', error); return; }
      // O aviso por e-mail é disparado por trigger no banco, sem depender do navegador.
      load();
    });

    return ticket;
  }, [session, load]);

  const updateTicketStatus = useCallback((id: string, status: ChamadoStatus | SugestaoStatus) => {
    setTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    supabase.from('support_tickets').update({ status }).eq('id', id).then(() => load());
  }, [load]);

  const sendMessage = useCallback(async (ticketId: string, body: string, authorRole: 'admin' | 'cliente') => {
    if (!session?.user) return false;
    const { data, error } = await supabase
      .from('support_messages')
      .insert({ ticket_id: ticketId, user_id: session.user.id, author_role: authorRole, body })
      .select('id')
      .maybeSingle();
    if (error) {
      console.error('Erro ao enviar mensagem:', error);
      return false;
    }
    void data;
    // O e-mail da resposta é disparado por trigger no banco.
    load();
    return true;
  }, [session, load]);

  const markTicketRead = useCallback(async (ticketId: string) => {
    await supabase.rpc('support_mark_read', { _ticket_id: ticketId });
    setTickets(prev => prev.map(t => t.id === ticketId
      ? { ...t, ...(user?.isAdmin ? { unreadForAdmin: false } : { unreadForUser: false }) }
      : t));
  }, [user?.isAdmin]);

  const myTickets = React.useMemo(
    () => tickets.filter(t => t.userId && t.userId === session?.user?.id),
    [tickets, session?.user?.id],
  );

  return (
    <SupportContext.Provider value={{ tickets, myTickets, loading, addTicket, updateTicketStatus, sendMessage, markTicketRead, refresh: load }}>
      {children}
    </SupportContext.Provider>
  );
};

export const useSupport = () => {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupport must be used within SupportProvider');
  return ctx;
};
